/**
 * Typed React Query hooks for the admin app. Centralizes query keys + invalidation
 * so each page just calls `useWorkers()` / `useCreateWorker()` etc.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CustomerCreate,
  WorkerCreate,
  JobCreate,
} from '@fin/shared/schemas';
import type { JobStatus } from '@fin/shared/enums';

import { api } from './api';

interface ListResponse<T> {
  items: T[];
  /** Cursor for the next page, when more results exist. */
  nextCursor?: string;
}

export interface Worker {
  id: string;
  name: string;
  phone: string;
  email: string;
  employmentType: 'full_time' | 'part_time' | 'contractor';
  services: readonly string[];
  homeArea?: string;
  active: boolean;
  cognitoSub?: string;
  /** Set by listWorkers — reflects Cognito UserStatus.
   *  - `invited`: Cognito user exists, still has temp password (FORCE_CHANGE_PASSWORD)
   *  - `active`: worker has signed in and set their own password (CONFIRMED)
   *  - `unknown`: Cognito lookup failed
   *  - `no_account`: no Cognito user record found */
  loginStatus?: 'invited' | 'active' | 'unknown' | 'no_account';
  /** ISO timestamp of the last authenticated API call from this worker. */
  lastSeenAt?: string;
  createdAt: string;
  updatedAt: string;
}

/** /workers POST returns this — `credsEmailed` indicates whether Cognito
 *  successfully emailed the worker their temporary password. */
export interface WorkerCreated extends Worker {
  credsEmailed?: boolean;
}

export interface Customer {
  id: string;
  name: string;
  phone: string;
  whatsappPhone?: string;
  email?: string;
  address: {
    building?: string;
    unit?: string;
    street?: string;
    area: string;
    emirate: string;
    makaniNumber?: string;
  };
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Job {
  id: string;
  customerId: string;
  serviceType: string;
  priority: string;
  scheduledAt: string;
  /** Booked for the day with no committed time slot. */
  anytime?: boolean;
  estimatedDurationMinutes: number;
  description?: string;
  internalNotes?: string;
  /** Free-text notes written by the assigned worker from the field. */
  workerNotes?: string;
  assignedWorkerId?: string;
  /** Multi-worker assignment. Primary = assignedWorkerIds[0]. The legacy
   *  `assignedWorkerId` field always mirrors the primary id. */
  assignedWorkerIds?: string[];
  status: JobStatus;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  /** Worker acknowledgement of an overdue job. */
  overdueAck?: { workerId: string; at: string; etaMinutes: number; note?: string };
  /** Latest hold reason (admin-visible only). Cleared when status leaves on_hold. */
  holdReason?: string;
  holdAt?: string;
  holdBy?: string;
}

// Workers ────────────────────────────────────────────────────────────────────
export function useWorkers() {
  return useQuery({
    queryKey: ['workers'],
    queryFn: () => api<ListResponse<Worker>>('/workers').then((r) => r.items),
    // Refresh every 20s so the Presence column reflects live activity.
    refetchInterval: 20_000,
    refetchIntervalInBackground: false,
  });
}

export function useCreateWorker() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: WorkerCreate) =>
      api<WorkerCreated>('/workers', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workers'] }),
  });
}

export function usePatchWorker() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Worker> }) =>
      api<Worker>(`/workers/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workers'] }),
  });
}

export function useResendWorkerInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ resent: boolean; email: string }>(`/workers/${id}/resend-invite`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workers'] }),
  });
}

export function useDeleteWorker() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ deleted: boolean; unassignedJobs: number }>(`/workers/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workers'] });
      qc.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
}

export function useWorker(id: string | undefined) {
  return useQuery({
    queryKey: ['worker', id],
    queryFn: () => api<Worker>(`/workers/${id}`),
    enabled: !!id,
  });
}

export function useJobsByWorker(workerId: string | undefined) {
  return useQuery({
    queryKey: ['jobs-by-worker', workerId],
    queryFn: () => api<ListResponse<Job>>(`/jobs?workerId=${workerId}`).then((r) => r.items),
    enabled: !!workerId,
  });
}

// Customers ──────────────────────────────────────────────────────────────────
export function useCustomers(q?: string) {
  const trimmed = q?.trim() ?? '';
  return useQuery({
    queryKey: ['customers', trimmed],
    queryFn: () =>
      api<ListResponse<Customer>>(
        trimmed ? `/customers?q=${encodeURIComponent(trimmed)}` : '/customers',
      ).then((r) => r.items),
  });
}

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CustomerCreate) =>
      api<Customer>('/customers', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
  });
}

export function useCustomer(id: string | undefined) {
  return useQuery({
    queryKey: ['customer', id],
    queryFn: () => api<Customer>(`/customers/${id}`),
    enabled: !!id,
  });
}

export function usePatchCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Customer> }) =>
      api<Customer>(`/customers/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['customer', vars.id] });
      qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export function useDeleteCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ deleted: boolean }>(`/customers/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
  });
}

// Jobs ───────────────────────────────────────────────────────────────────────
/**
 * Jobs for a status, with an optional scheduledAt window and cursor
 * pagination. Completed/cancelled accumulate forever, so without a range +
 * "load more" anything past the server page cap was silently unreachable.
 */
export function useJobs(
  status: JobStatus = 'scheduled',
  range?: { from?: string; to?: string },
) {
  return useInfiniteQuery({
    queryKey: ['jobs', status, range?.from ?? '', range?.to ?? ''],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams({ status });
      if (range?.from) qs.set('from', range.from);
      if (range?.to) qs.set('to', range.to);
      if (pageParam) qs.set('cursor', pageParam);
      return api<ListResponse<Job>>(`/jobs?${qs.toString()}`);
    },
    getNextPageParam: (last) => last.nextCursor,
  });
}

export function useJob(id: string | undefined) {
  return useQuery({
    queryKey: ['job', id],
    queryFn: () => api<Job>(`/jobs/${id}`),
    enabled: !!id,
  });
}

/**
 * Flattened single-page view of useJobs for callers that just want an array
 * (e.g. the Today board, which aggregates several statuses and never paginates).
 */
export function useJobList(status: JobStatus) {
  const q = useJobs(status);
  const items = (q.data?.pages ?? []).flatMap((pg) => pg.items);
  return { ...q, data: q.data ? items : undefined };
}

export interface JobLogEntry {
  id: string;
  jobId: string;
  action: 'status' | 'assigned_worker' | 'scheduled_at';
  from?: string;
  to?: string;
  reason?: string;
  actorId: string;
  actorEmail: string;
  at: string;
}

export function useJobLog(id: string | undefined) {
  return useQuery({
    queryKey: ['job-log', id],
    queryFn: () => api<ListResponse<JobLogEntry>>(`/jobs/${id}/log`).then((r) => r.items),
    enabled: !!id,
  });
}

export interface JobProof {
  id: string;
  key: string;
  kind: 'before' | 'after' | 'parts' | 'signature' | 'video' | 'intake_photo' | 'intake_video';
  caption?: string;
  uploadedBy: string;
  uploadedAt: string;
  /** Playable URL — the transcoded H.264 MP4 for videos once ready, else the original. */
  url: string;
  /** Videos only: presigned URL of the original upload (for download). */
  originalUrl?: string;
  /** True for video proof items. */
  isVideo?: boolean;
  /** Video still transcoding to a browser-safe MP4 — `url` may not play yet. */
  processing?: boolean;
}

export type ProofKind = JobProof['kind'];

interface ProofUploadUrlResponse {
  uploadUrl: string;
  key: string;
  proofId: string;
  kind: ProofKind;
  expiresIn: number;
}

/**
 * Two-step proof upload: ask API for a presigned PUT URL, send the file
 * straight to S3, then record the key on the job. Used by both the worker
 * app and the admin Create-Job intake media flow.
 */
export async function uploadProofForJob(
  jobId: string,
  file: File,
  kind: ProofKind,
  caption?: string,
): Promise<JobProof> {
  const contentType = file.type || 'application/octet-stream';
  const presigned = await api<ProofUploadUrlResponse>(`/jobs/${jobId}/proof-url`, {
    method: 'POST',
    body: JSON.stringify({ contentType, kind }),
  });
  const putRes = await fetch(presigned.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });
  if (!putRes.ok) throw new Error(`Upload failed (${putRes.status}).`);
  return api<JobProof>(`/jobs/${jobId}/proof`, {
    method: 'POST',
    body: JSON.stringify({ key: presigned.key, kind, caption }),
  });
}

export function useJobProof(id: string | undefined) {
  return useQuery({
    queryKey: ['job-proof', id],
    queryFn: () => api<ListResponse<JobProof>>(`/jobs/${id}/proof`).then((r) => r.items),
    enabled: !!id,
    // Presigned URLs expire in 10 min; refresh a bit before.
    staleTime: 8 * 60 * 1000,
  });
}

/**
 * Fetch proof items with longer-lived presigned URLs (suitable for sharing
 * with the customer over WhatsApp). Caps at 60 minutes server-side.
 */
export async function fetchProofForShare(jobId: string, expiresInSeconds = 3600): Promise<JobProof[]> {
  const res = await api<ListResponse<JobProof>>(`/jobs/${jobId}/proof?expiresIn=${expiresInSeconds}`);
  return res.items;
}

export interface ShareLink {
  proofId: string;
  kind: ProofKind;
  shortUrl: string;
}

/**
 * Mint short branded URLs for the given proof items so the customer
 * receives a clean WhatsApp message instead of a 1500-char signed S3 URL.
 */
export async function createShareLinks(
  jobId: string,
  proofIds: string[],
  expiresInSeconds = 3600,
): Promise<ShareLink[]> {
  const idempotencyKey =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random()}`;
  const res = await api<{ links: ShareLink[]; expiresIn: number }>(
    `/jobs/${jobId}/share-links`,
    {
      method: 'POST',
      body: JSON.stringify({ proofIds, expiresIn: expiresInSeconds }),
      headers: { 'Idempotency-Key': idempotencyKey },
    },
  );
  return res.links;
}

// Invoices ────────────────────────────────────────────────────────────────────
export interface InvoiceLineItem {
  description: string;
  quantity: number;
  unitPriceFils: number;
  lineTotalFils: number;
}

export interface Invoice {
  id: string;
  number: string;
  jobId: string;
  customerId: string;
  serviceType: string;
  items: InvoiceLineItem[];
  subtotalFils: number;
  vatRate: number;
  vatFils: number;
  totalFils: number;
  currency: 'AED';
  status: 'draft' | 'sent' | 'paid';
  createdAt: string;
  createdBy: string;
  notes?: string;
  /** Presigned PDF URL (short-lived). */
  url: string;
  /** Branded short redirect URL (preferred for messaging). */
  shortUrl?: string;
}

export interface InvoiceCreateBody {
  items: { description: string; quantity: number; unitPriceFils: number }[];
  notes?: string;
  vatRate?: number;
}

export function useJobInvoices(id: string | undefined) {
  return useQuery({
    queryKey: ['job-invoices', id],
    queryFn: () => api<ListResponse<Invoice>>(`/jobs/${id}/invoices`).then((r) => r.items),
    enabled: !!id,
    staleTime: 8 * 60 * 1000,
  });
}

export function useCreateInvoice(jobId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InvoiceCreateBody) =>
      api<Invoice>(`/jobs/${jobId}/invoices`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['job-invoices', jobId] });
    },
  });
}

/** Fetch a fresh long-lived (1h) presigned URL for sharing over WhatsApp. */
export async function fetchInvoiceShareUrl(invoiceId: string): Promise<Invoice> {
  return api<Invoice>(`/invoices/${invoiceId}`);
}

export interface InvoiceListRow extends Invoice {
  customerName: string;
}

export interface InvoiceListFilters {
  from?: string;        // ISO date (YYYY-MM-DD)
  to?: string;          // ISO date (YYYY-MM-DD), exclusive upper
  status?: 'draft' | 'sent' | 'paid';
  customerId?: string;
  q?: string;
  limit?: number;
  cursor?: string;
}

export interface InvoiceListPage {
  items: InvoiceListRow[];
  cursor?: string;
}

export function useInvoices(filters: InvoiceListFilters) {
  const qs = new URLSearchParams();
  if (filters.from) qs.set('from', filters.from);
  if (filters.to) qs.set('to', filters.to);
  if (filters.status) qs.set('status', filters.status);
  if (filters.customerId) qs.set('customerId', filters.customerId);
  if (filters.q) qs.set('q', filters.q);
  if (filters.limit) qs.set('limit', String(filters.limit));
  if (filters.cursor) qs.set('cursor', filters.cursor);
  const suffix = qs.toString();
  return useQuery({
    queryKey: ['invoices', filters],
    queryFn: () => api<InvoiceListPage>(`/invoices${suffix ? `?${suffix}` : ''}`),
    staleTime: 30 * 1000,
  });
}

export function useUpdateInvoiceStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: Invoice['status'] }) =>
      api<Invoice>(`/invoices/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['job-invoices'] });
      void vars;
    },
  });
}

export function useDeleteInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<{ deleted: true; id: string }>(`/invoices/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['job-invoices'] });
    },
  });
}

export function useCreateJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: JobCreate) => api<Job>('/jobs', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs'] }),
  });
}

export function usePatchJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<Job & { reason?: string }> }) =>
      api<Job>(`/jobs/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: (_data, vars) => {
      // Invalidate list views, the detail card, and the audit log so the
      // detail page reflects status / scheduledAt / assignment changes
      // immediately (e.g. after Reschedule from on_hold → assigned).
      qc.invalidateQueries({ queryKey: ['jobs'] });
      qc.invalidateQueries({ queryKey: ['job', vars.id] });
      qc.invalidateQueries({ queryKey: ['job-log', vars.id] });
    },
  });
}

// Notifications ──────────────────────────────────────────────────────────────
export interface Notification {
  id: string;
  kind: string;
  title: string;
  body: string;
  url?: string;
  createdAt: string;
  readAt?: string;
  data?: Record<string, unknown>;
}

export function useNotifications() {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: () => api<ListResponse<Notification>>('/notifications').then((r) => r.items),
    refetchInterval: 60_000,
  });
}

export function useMarkNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      api('/notifications/read', { method: 'POST', body: JSON.stringify({ ids }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

// Feedback ───────────────────────────────────────────────────────────────────
export interface FeedbackEntry {
  jobId: string;
  source: 'google' | 'private';
  rating?: number;
  comment?: string;
  contact?: string;
  createdAt: string;
  /** Enriched server-side from the linked job + customer records. */
  customerId?: string;
  customerName?: string;
  customerPhone?: string;
}

export function useFeedback() {
  return useQuery({
    queryKey: ['feedback'],
    queryFn: () => api<ListResponse<FeedbackEntry>>('/feedback').then((r) => r.items),
    refetchInterval: 60_000,
  });
}


// -- Blog content (Phase 3) ------------------------------------------------

import type {
  PostCreate,
  PostPreview,
  PostUpdate,
} from '@fin/shared/schemas';
import type { PostStatus } from '@fin/shared/enums';

export interface BlogPost {
  postId: string;
  slug: string;
  title: string;
  description: string;
  status: PostStatus;
  category?: string;
  tags?: string[];
  focusKeyword?: string;
  secondaryKeywords?: string[];
  author?: string;
  hero?: { src: string; alt: string; width?: number; height?: number } | null;
  publishedAt?: string;
  publishAt?: string;
  updatedAt: string;
  createdAt?: string;
  createdBy?: string;
  currentVersionId?: string;
  latestBuildId?: string;
  lastError?: string;
}

export interface BlogPostWithBody extends BlogPost {
  body: string;
}

export interface BlogValidationIssue {
  code: string;
  message: string;
  field?: string;
}

export interface BlogValidationResult {
  ok: boolean;
  errors: BlogValidationIssue[];
  warnings: BlogValidationIssue[];
  score: number;
}

export interface BlogPreview {
  html: string;
  warnings: string[];
  readingMinutes: number;
  wordCount: number;
}

export interface BlogVersion {
  versionId: string;
  isLatest: boolean;
  lastModified: string | null;
  size: number;
}

export interface BlogBuild {
  buildId: string;
  postId: string;
  mode: 'incremental' | 'full';
  status: 'pending' | 'running' | 'success' | 'failed';
  startedAt: string;
  finishedAt?: string;
  durationMs?: number;
  triggeredBy?: string;
  publishedUrl?: string;
  invalidationId?: string;
  invalidationPaths?: string[];
  errorMessage?: string;
}

export function useContentPosts(status?: PostStatus) {
  return useQuery({
    queryKey: ['content-posts', status ?? 'all'],
    queryFn: () =>
      api<ListResponse<BlogPost>>(status ? `/content/posts?status=${status}` : '/content/posts').then(
        (r) => r.items,
      ),
  });
}

export function useContentPost(id: string | undefined) {
  return useQuery({
    queryKey: ['content-post', id],
    queryFn: () => api<BlogPostWithBody>(`/content/posts/${id}`),
    enabled: !!id,
  });
}

/** Upload an editor-chosen image (e.g. the hero banner) for a post: presign,
 *  then PUT straight to S3. Resolves to the `/media/<slug>/<file>` src. */
export function useUploadPostMedia() {
  return useMutation({
    mutationFn: async ({ id, file, filename }: { id: string; file: File; filename: string }) => {
      const signed = await api<{ url: string; key: string; src: string }>(`/content/posts/${id}/media-url`, {
        method: 'POST',
        body: JSON.stringify({ filename, contentType: file.type }),
      });
      const put = await fetch(signed.url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);
      return signed.src;
    },
  });
}

export function useCreateContentPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PostCreate) =>
      api<BlogPost>('/content/posts', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['content-posts'] }),
  });
}

export function useUpdateContentPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: PostUpdate }) =>
      api<BlogPost>(`/content/posts/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['content-post', vars.id] });
      qc.invalidateQueries({ queryKey: ['content-posts'] });
    },
  });
}

export function useDeleteContentPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/content/posts/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['content-posts'] }),
  });
}

export function usePreviewContentPost() {
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: PostPreview }) =>
      api<BlogPreview>(`/content/posts/${id}/preview`, {
        method: 'POST',
        body: JSON.stringify(patch),
      }),
  });
}

export function useValidateContentPost() {
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: PostPreview }) =>
      api<BlogValidationResult>(`/content/posts/${id}/validate`, {
        method: 'POST',
        body: JSON.stringify(patch),
      }),
  });
}

export function usePublishContentPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, skipValidation }: { id: string; skipValidation?: boolean }) =>
      api<BlogPost & { buildTriggered: boolean }>(`/content/posts/${id}/publish`, {
        method: 'POST',
        body: JSON.stringify({ skipValidation: skipValidation ?? false }),
      }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['content-post', vars.id] });
      qc.invalidateQueries({ queryKey: ['content-posts'] });
      qc.invalidateQueries({ queryKey: ['content-builds'] });
    },
  });
}

export function useUnpublishContentPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<BlogPost & { buildTriggered: boolean }>(`/content/posts/${id}/unpublish`, {
        method: 'POST',
      }),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['content-post', id] });
      qc.invalidateQueries({ queryKey: ['content-posts'] });
      qc.invalidateQueries({ queryKey: ['content-builds'] });
    },
  });
}

export function useContentVersions(id: string | undefined) {
  return useQuery({
    queryKey: ['content-versions', id],
    queryFn: () => api<ListResponse<BlogVersion>>(`/content/posts/${id}/versions`).then((r) => r.items),
    enabled: !!id,
  });
}

export function useRestoreContentVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, versionId }: { id: string; versionId: string }) =>
      api<BlogPost>(`/content/posts/${id}/restore`, {
        method: 'POST',
        body: JSON.stringify({ versionId }),
      }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['content-post', vars.id] });
      qc.invalidateQueries({ queryKey: ['content-versions', vars.id] });
    },
  });
}

export function useContentBuilds() {
  return useQuery({
    queryKey: ['content-builds'],
    queryFn: () => api<ListResponse<BlogBuild>>('/content/builds').then((r) => r.items),
    refetchInterval: 15_000,
  });
}

export function useTriggerFullBuild() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ invoked: boolean }>('/content/builds/full', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['content-builds'] }),
  });
}

/** Re-render and re-invalidate the CDN for a single published post. */
export function useRebuildContentPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ buildTriggered: boolean }>(`/content/posts/${id}/rebuild`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['content-builds'] }),
  });
}

export interface AiUsageDay {
  ymd: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  imagesGenerated: number;
  costUsd: number;
}
export interface AiUsageUser {
  userEmail: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  imagesGenerated: number;
  costUsd: number;
}
export interface AiUsageSummary {
  rangeDays: number;
  dailyCallCap: number;
  totals: {
    calls: number;
    inputTokens: number;
    outputTokens: number;
    imagesGenerated: number;
    costUsd: number;
  };
  today: { calls: number; costUsd: number };
  days: AiUsageDay[];
  users: AiUsageUser[];
}

export function useAiUsage(days: number = 30) {
  return useQuery({
    queryKey: ['ai-usage', days],
    queryFn: () => api<AiUsageSummary>(`/content/ai/usage?days=${days}`),
    refetchInterval: 60_000,
  });
}

// -- Blog AI authoring hooks ---------------------------------------------

export interface AiTitlesResponse { titles: string[] }
export interface AiMetaResponse { description: string }
export interface AiBodyResponse { markdown: string; wordCount: number }
export interface AiHeroResponse { src: string; alt: string; bytes: number }
export interface AiFullResponse {
  title: string;
  description: string;
  markdown: string;
  suggestedSlug: string;
  wordCount: number;
}

export function useAiSuggestTitles() {
  return useMutation({
    mutationFn: (body: { topic: string; focusKeyword?: string; count?: number }) =>
      api<AiTitlesResponse>('/content/ai/suggest-titles', { method: 'POST', body: JSON.stringify(body) }),
  });
}

export function useAiGenerateMeta() {
  return useMutation({
    mutationFn: (body: { title: string; body?: string; focusKeyword?: string }) =>
      api<AiMetaResponse>('/content/ai/generate-meta', { method: 'POST', body: JSON.stringify(body) }),
  });
}

export function useAiGenerateBody() {
  return useMutation({
    mutationFn: (body: {
      title: string;
      outline?: string;
      focusKeyword?: string;
      secondaryKeywords?: string[];
      words?: number;
    }) => api<AiBodyResponse>('/content/ai/generate-body', { method: 'POST', body: JSON.stringify(body) }),
  });
}

export function useAiGenerateHero() {
  return useMutation({
    mutationFn: (body: { slug: string; prompt: string; alt?: string }) =>
      api<AiHeroResponse>('/content/ai/generate-hero', { method: 'POST', body: JSON.stringify(body) }),
  });
}

export function useAiGenerateFull() {
  return useMutation({
    mutationFn: (body: { topic: string; focusKeyword?: string; words?: number }) =>
      api<AiFullResponse>('/content/ai/generate-full', { method: 'POST', body: JSON.stringify(body) }),
  });
}
